/**
 * scanDomUnsavedInput
 *
 * Generic, low-false-positive DOM safety net for the "auto-reload on new
 * version" feature's unsaved-work guard (see App.tsx / index.html).
 *
 * The primary signal is `useUnsavedChangesStore`, an opt-in registry that
 * precise editors populate via `useUnsavedGuard`. That registry only
 * protects forms that have been explicitly wired up. Any un-wired form is
 * silently unprotected against a background auto-reload wiping its state.
 *
 * This function is a coarse fallback that scans the live DOM for signals
 * that are cheap to check and very unlikely to false-positive:
 *
 * 1. Any `<input type="password">` with a non-empty value — a filled
 *    credential field is always worth protecting, regardless of whether
 *    it lives inside a recognized dialog.
 * 2. Any OPEN modal/dialog (`role="dialog"`, `aria-modal="true"`, or an
 *    open `<dialog>` element) containing a filled text-like input or
 *    textarea — covers un-wired modals without needing app knowledge.
 *
 * Deliberately NOT scanned (to keep false positives low so the feature can
 * still fire under normal conditions):
 * - Plain, top-level (non-dialog) single-line inputs — would trip on
 *   persistent search/filter boxes that always have a value.
 * - `contenteditable` elements — placeholder/boilerplate text in rich
 *   editors reads as "content" and would false-positive; editors like
 *   PostCreator are already precisely wired via the registry.
 *
 * A false positive here only suppresses one auto-reload attempt (the user
 * sees the manual "update available" banner instead) — safe by design.
 */
// Some drawers (WorkspaceDetailDrawer, AnalyticsFilterDrawer, PostFilterDrawer,
// VariationFilterDrawer) render `role="dialog" aria-modal="true"` UNCONDITIONALLY
// and hide themselves via CSS (opacity/translate/display) instead of unmounting.
// DOM presence of a dialog/field therefore does NOT mean it is actually open/shown
// to the user — a mounted-but-closed drawer with a filled field would otherwise be
// wrongly counted as unsaved work. Only genuinely visible elements should count.
function isVisible(el: Element): boolean {
  // Fail safe toward "protect" if we can't determine visibility (SSR/safety).
  if (typeof window === 'undefined' || !window.getComputedStyle) return true;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  const style = window.getComputedStyle(el as HTMLElement);
  if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
    return false;
  }
  if (parseFloat(style.opacity || '1') === 0) return false;
  return true;
}

export function scanDomUnsavedInput(): boolean {
  if (typeof document === 'undefined') return false;

  // 1) Any password field with a value → always protect (credentials).
  const pwFields = document.querySelectorAll<HTMLInputElement>('input[type="password"]');
  for (const el of pwFields) {
    if (!isVisible(el)) continue;
    if (el.value && el.value.trim().length > 0) return true;
  }

  // 2) Any OPEN modal/dialog containing a filled text input or textarea.
  const dialogs = document.querySelectorAll<HTMLElement>(
    '[role="dialog"], [aria-modal="true"], dialog[open]'
  );
  for (const dialog of dialogs) {
    if (!isVisible(dialog)) continue;
    const fields = dialog.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'textarea, input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]):not([type="button"]):not([type="search"]):not([type="range"]):not([type="file"])'
    );
    for (const field of fields) {
      if (!isVisible(field)) continue;
      if (field.value && field.value.trim().length > 0) return true;
    }
  }

  return false;
}
